/**
 * The public web store's render input.
 *
 * Every template reads from this object and nothing else — no API calls, no
 * context, no globals. That's the whole point: once the storefront settings
 * in PayIQ Business are wired to a backend, "settings change → store
 * updates" is just this object arriving with different values. Nothing in a
 * template needs to know where it came from.
 *
 * Deliberately NOT connected to anything yet. The business app's backend
 * shape is still being decided, so binding now would bake in guesses.
 */

export type TemplateId = 'boutique' | 'market' | 'studio';

export interface StorefrontProduct {
  id: string;
  name: string;
  /** Naira, whole units. */
  price: number;
  compareAt?: number;
  category: string;
  /** Short selling line. Templates use it differently — Studio leans on it,
   *  Boutique mostly ignores it, Market hides it. */
  blurb?: string;
  /** Placeholder art. Real stores will carry image URLs; until then each
   *  product gets a deterministic gradient so a template's grid reads as a
   *  grid of *different* things rather than repeated grey boxes. */
  swatch: [string, string];
  soldOut?: boolean;
  badge?: string;
}

export interface StorefrontConfig {
  templateId: TemplateId;
  name: string;
  bio: string;
  /** Hex. Drives every accented surface in every template. */
  accent: string;
  domain: string;
  category: string;
  location: string;
  hours: string;
  /** Whether prices are public — the business app's "Show prices to
   *  visitors" toggle. Templates switch their calls to action when false. */
  showPrices: boolean;
  currency: string;
  products: StorefrontProduct[];
  collections: string[];
  announcement?: string;
}

/** White text needs a dark enough accent; several templates put type
 *  directly on it. Mirrors the guard in the business app's colour picker. */
export function luminance(hex: string): number {
  const v = hex.replace('#', '');
  const full =
    v.length === 3
      ? `${v[0]}${v[0]}${v[1]}${v[1]}${v[2]}${v[2]}`
      : v.padEnd(6, '0').slice(0, 6);
  const channels = [0, 2, 4].map((i) => {
    const c = parseInt(full.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
}

/**
 * Text colour that survives on `hex`. Templates call this instead of
 * hard-coding white, so a merchant who picks a pale yellow accent gets dark
 * type on their buttons rather than an invisible label.
 */
export function onAccent(hex: string): string {
  return luminance(hex) > 0.45 ? '#16191D' : '#FFFFFF';
}

/** Contrast of white text on `hex`, for warning when an accent is too pale
 *  to carry light type on the page furniture around it. */
export function contrastOnWhite(hex: string): number {
  return 1.05 / (luminance(hex) + 0.05);
}

export function formatMoney(amount: number, currency = '₦'): string {
  return `${currency}${amount.toLocaleString('en-NG')}`;
}
